import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, Users, BellRing, LogOut, Bell, Menu, CheckCheck, Settings as SettingsIcon,
  TrendingUp, Wrench, KanbanSquare, Mail, Sparkles, Mic, Receipt, MessagesSquare,
} from "lucide-react";
import { CalendarCheck, Briefcase } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePublicSettings } from "@/context/PublicSettingsContext";
import api from "@/lib/api";
import { ROLE_LABELS, ROLE_COLORS } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertPrefsButton } from "@/components/AlertPrefs";
import { flushVoiceQueue } from "@/lib/offlineQueue";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const navFor = (role, features = {}, isSuper = false) => {
  const base = [{ to: "/", label: "Dashboard", icon: LayoutDashboard }];
  if (role === "tenant")
    return [{ to: "/", label: "My Home", icon: LayoutDashboard }];
  if (role === "landlord")
    return [...base, { to: "/properties", label: "My Properties", icon: Building2 }, { to: "/issues", label: "Issues", icon: Wrench }, { to: "/expenses", label: "Expenses", icon: Receipt }, { to: "/conversations", label: "Conversations", icon: MessagesSquare }];
  const staff = [
    ...base,
    { to: "/properties", label: "Properties", icon: Building2 },
    { to: "/landlords", label: "Owners", icon: Users },
  ];
  if (features.investor_matching !== false) staff.push({ to: "/investors", label: "Owner Leads", icon: TrendingUp });
  staff.splice(3, 0, { to: "/corporate", label: "Corporate", icon: Briefcase });
  staff.push({ to: "/tasks", label: "Tasks", icon: KanbanSquare });
  staff.push({ to: "/cleaners", label: "Cleaners", icon: Sparkles });
  staff.push({ to: "/issues", label: "Issues", icon: Wrench });
  staff.push({ to: "/expenses", label: "Expenses", icon: Receipt });
  staff.push({ to: "/conversations", label: "Conversations", icon: MessagesSquare });
  staff.push({ to: "/reminders", label: "Reminders", icon: BellRing });
  staff.push({ to: "/newsletter", label: "Newsletter", icon: Mail });
  if (role === "admin") staff.push({ to: "/settings", label: "Settings", icon: SettingsIcon });
  staff.push({ to: "/voice-notes", label: "Voice Notes", icon: Mic });
  return staff;
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const loadNotes = () => api.get("/notifications").then((r) => setNotes(r.data)).catch(() => {});
  const [dueCount, setDueCount] = useState(0);
  const loadDue = () => {
    if (!["admin", "operations", "compliance"].includes(user.role)) return;
    api.get("/tasks/due-count").then((r) => setDueCount(r.data.due_soon || 0)).catch(() => {});
  };
  const [convoCount, setConvoCount] = useState(0);
  const loadConvos = () => {
    if (user.role === "tenant") return;
    api.get("/conversations/unread-count").then((r) => setConvoCount(r.data.count || 0)).catch(() => {});
  };
  useEffect(() => {
    loadNotes();
    loadDue();
    loadConvos();
    const t = setInterval(() => { loadNotes(); loadDue(); loadConvos(); }, 20000);
    window.addEventListener("conversations-changed", loadConvos);
    return () => { clearInterval(t); window.removeEventListener("conversations-changed", loadConvos); };
  }, []);
  useEffect(() => { loadConvos(); }, [loc.pathname, loc.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user.is_super_admin) return undefined;
    const flush = () => flushVoiceQueue().then((n) => { if (n) toast.success(`${n} offline voice note${n === 1 ? "" : "s"} uploaded`); }).catch(() => {});
    flush();
    window.addEventListener("online", flush);
    return () => window.removeEventListener("online", flush);
  }, [user.is_super_admin]);

  const unread = notes.filter((n) => !n.read).length;
  const { features } = usePublicSettings();
  const links = navFor(user.role, features, !!user.is_super_admin);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    loadNotes();
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="hidden md:flex w-60 flex-col border-r border-border bg-white/60 backdrop-blur-sm sticky top-0 h-screen">
        <div className="px-5 h-16 flex items-center gap-2 border-b border-border">
          <div className="h-8 w-8 rounded-md bg-[#18181A] flex items-center justify-center">
            <Building2 className="h-4 w-4 text-white" />
          </div>
          <span className="font-head text-lg font-extrabold tracking-tight">Bides</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {links.map((l) => {
            const active = loc.pathname === l.to || (l.to !== "/" && loc.pathname.startsWith(l.to));
            const Icon = l.icon;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  active
                    ? "bg-[#18181A] text-white"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1">{l.label}</span>
                {l.to === "/tasks" && dueCount > 0 && <span data-testid="tasks-due-badge" title="Your tasks due soon or overdue" className="text-[0.6rem] font-bold px-1.5 py-0.5 rounded-full bg-[#9E382A] text-white">{dueCount}</span>}
                {l.to === "/conversations" && convoCount > 0 && <span data-testid="conversations-unread-badge" title="Unread conversations" className="text-[0.6rem] font-bold px-1.5 py-0.5 rounded-full bg-[#D98324] text-white">{convoCount}</span>}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-2 mb-2">
            <p className="text-sm font-semibold truncate">{user.name}</p>
            <span className={`inline-block mt-1 text-[0.65rem] px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[user.role]}`}>
              {ROLE_LABELS[user.role]}
            </span>
          </div>
          {["admin", "operations"].includes(user.role) && <AlertPrefsButton />}
          <Button
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start text-muted-foreground"
            onClick={() => { logout(); nav("/login"); }}
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-white/70 backdrop-blur-sm flex items-center justify-between px-5 sticky top-0 z-20">
          <div className="flex items-center gap-2 md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-button" aria-label="Open menu" className="p-2 -ml-2 rounded-md hover:bg-secondary transition-colors">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 flex flex-col">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <SheetDescription className="sr-only">Main navigation links</SheetDescription>
                <div className="px-5 h-16 flex items-center gap-2 border-b border-border shrink-0">
                  <div className="h-8 w-8 rounded-md bg-[#18181A] flex items-center justify-center">
                    <Building2 className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-head text-lg font-extrabold tracking-tight">Bides</span>
                </div>
                <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                  {links.map((l) => {
                    const active = loc.pathname === l.to || (l.to !== "/" && loc.pathname.startsWith(l.to));
                    const Icon = l.icon;
                    return (
                      <Link
                        key={l.to}
                        to={l.to}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`mnav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                          active ? "bg-[#18181A] text-white" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="flex-1">{l.label}</span>
                        {l.to === "/tasks" && dueCount > 0 && <span data-testid="mtasks-due-badge" className="text-[0.6rem] font-bold px-1.5 py-0.5 rounded-full bg-[#9E382A] text-white">{dueCount}</span>}
                        {l.to === "/conversations" && convoCount > 0 && <span data-testid="mconversations-unread-badge" className="text-[0.6rem] font-bold px-1.5 py-0.5 rounded-full bg-[#D98324] text-white">{convoCount}</span>}
                      </Link>
                    );
                  })}
                </nav>
                <div className="p-3 border-t border-border shrink-0">
                  <div className="px-2 mb-2">
                    <p className="text-sm font-semibold truncate">{user.name}</p>
                    <span className={`inline-block mt-1 text-[0.65rem] px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[user.role]}`}>
                      {ROLE_LABELS[user.role]}
                    </span>
                  </div>
                  {["admin", "operations"].includes(user.role) && <AlertPrefsButton testId="mobile-alert-prefs-button" />}
                  <Button data-testid="mobile-logout-button" variant="ghost" className="w-full justify-start text-muted-foreground" onClick={() => { setMobileOpen(false); logout(); nav("/login"); }}>
                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            <span className="font-head font-extrabold">Bides</span>
          </div>
          <div className="hidden md:block overline text-muted-foreground">
            Property Onboarding CRM
          </div>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button data-testid="notifications-button" className="relative p-2 rounded-md hover:bg-secondary transition-colors">
                <Bell className="h-5 w-5" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#9E382A] text-white text-[0.6rem] flex items-center justify-center">
                    {unread}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <span className="overline">Notifications</span>
                <button data-testid="mark-all-read" onClick={markAll} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                  <CheckCheck className="h-3 w-3" /> Mark all
                </button>
              </div>
              <ScrollArea className="max-h-80">
                {notes.length === 0 && (
                  <p className="p-6 text-sm text-muted-foreground text-center">No notifications yet.</p>
                )}
                {notes.map((n) => (
                  <div
                    key={n.id}
                    data-testid={`notification-${n.id}`}
                    onClick={() => (n.link ? nav(n.link) : n.property_id && nav(`/properties/${n.property_id}`))}
                    className={`px-4 py-3 border-b border-border cursor-pointer hover:bg-secondary transition-colors ${!n.read ? "bg-accent/5" : ""}`}
                  >
                    <p className="text-sm font-medium">{n.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{n.message}</p>
                  </div>
                ))}
              </ScrollArea>
            </PopoverContent>
          </Popover>
        </header>
        <main className="flex-1 p-5 lg:p-8 max-w-[1500px] w-full mx-auto">{children}</main>
        {user.is_super_admin && !loc.pathname.startsWith("/voice-notes") && (
          <Link to="/voice-notes?record=1" data-testid="quick-record-fab" aria-label="Record a voice note"
            className="lg:hidden fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-[#18181A] text-white shadow-xl flex items-center justify-center active:scale-95 transition-transform">
            <Mic className="h-6 w-6" />
          </Link>
        )}
      </div>
    </div>
  );
}
